import { Injectable, OnDestroy } from '@angular/core';
import { RoutePlanning } from '../../interfaces/route-planning/@route-planning.interface';
import { Section } from '../../interfaces/route-planning/section.interface';
import { eSectionType } from '../../enums/route-planning/section-type.enum';
import {
  interval,
  Subscription,
  Observable,
  Subject,
  BehaviorSubject,
} from 'rxjs';
import { takeWhile, filter, switchMap, map, take, finalize } from 'rxjs/operators';
import { UserLocationService } from 'src/app/@shared/services/user-location/user-location.service';
import { MapService } from '../map/map.service';
import { Location } from '../../interfaces/route-planning/location.interface';
import { LogService } from 'src/app/@shared/services/log/log.service';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { AudioService } from 'src/app/@shared/services/audio/audio.service';
import {
  BUS_APPROACHING,
  DROP_OFF_APPROACHING,
} from '../../constants/multi-language.constants';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { BoardBusConfirmationPopupComponent } from '../../components/route-planner-results/results/source-destination/source-destination-route-tracking/board-bus-confirmation-popup/board-bus-confirmation-popup.component';
import { ScheduleService } from '../schedule/schedule.service';
import { RouteTrackingBannerService } from './route-tracking-banner.service';
import { Coordinate } from '../../interfaces/location/coordinate.interface';
// NOTE: Using global H from CDN 3.2 scripts loaded in index.html
declare const H: any;

export interface TrackingState {
  currentSectionIndex: number;
  currentActionIndex: number;
  isUserOnBus: boolean; // User confirmed boarding the bus
  boardedBusSectionIndex: number | null; // Index of section where user boarded the bus
}

export interface BoardingDebugState {
  updatedAt: string;
  /** Monotonic counter each location-based progress tick (~3s). */
  debugTick: number;
  /** `trackingState.currentSectionIndex` (UI / state machine). */
  trackingSectionIndex: number;
  /** Section inferred from GPS + schedule in `getCurrentSectionBasedOnLocation`. */
  detectedSectionIndex: number | null;
  /** Section index used for `GetNextRouteLocation` / boarding logic; null when not in that path. */
  boardingCheckSectionIndex: number | null;
  currentSectionType: string | null;
  isUserOnBus: boolean;
  boardedBusSectionIndex: number | null;
  userLocation: Coordinate | null;
  gpsAccuracyMeters: number | null;
  stopLocation: Coordinate | null;
  busLocation: Coordinate | null;
  distanceToBusMeters: number | null;
  distanceToStopMeters: number | null;
  closeToBus: boolean;
  closeToStop: boolean;
  busDeparted: boolean | null;
  minutesSinceDeparture: number | null;
  hasShownPopup: boolean;
  popupEmitted: boolean;
  routeDesc: string | null;
  stopCode: string | null;
  transportLineLabel: string | null;
  triggerSource: 'transit' | 'pedestrian-near-stop' | 'unknown';
  skipReason: string | null;
  routeId: string | null;
  routeSectionsCount: number | null;
  /** Distance to end of current pedestrian section (m), when applicable. */
  pedestrianToArrivalMeters: number | null;
  /** Distance to next transit boarding stop while on pedestrian leg (m). */
  distanceToNextTransitBoardingStopMeters: number | null;
  withinNextBoardingStopPrecheck: boolean;
  scheduledDepartureIso: string | null;
  /** Same departure instant, formatted in the device local timezone (for TZ sanity checks). */
  scheduledDepartureLocal: string | null;
  /** Device clock string and IANA zone (not UTC-only). */
  deviceNowLocal: string;
  deviceTimeZone: string;
  lastScheduleApiMessage: string | null;
  shownNotificationsCount: number;
  checkIntervalMs: number;
  thrBusBoardingM: number;
  thrPedCompleteM: number;
  thrNearStopPrecheckM: number;
}

@Injectable({
  providedIn: 'root',
})
export class RouteTrackingService implements OnDestroy {
  private currentRoute: RoutePlanning | null = null;
  private trackingSubscription: Subscription | null = null;
  private readonly NOTIFICATION_THRESHOLD_MINUTES = 2;
  /** “Bus approaching” by live vehicle position vs user GPS (catches early-running buses). */
  private readonly BUS_APPROACHING_PROXIMITY_METERS = 400;
  private readonly CHECK_INTERVAL_MS = 3000; // 3 seconds
  private readonly UPDATE_INTERVAL_MS = 10000; // keep in sync with map vehicle poll interval
  private readonly MAX_DISTANCE_FROM_ROUTE_METERS = 500; // 500 meters
  private readonly DROP_OFF_PROXIMITY_METERS = 500; // 500 meters proximity for drop-off notification
  /** Boarding popup: user is “close” to vehicle or boarding stop within this radius. */
  private readonly BUS_BOARDING_DISTANCE_METERS = 200;
  /**
   * Stop-only boarding prompt: only when scheduled departure is within this lead time (or already passed).
   * Otherwise at the stop alone, require proximity to the vehicle ({@link BUS_BOARDING_DISTANCE_METERS}).
   */
  private readonly BOARDING_STOP_TIME_WINDOW_BEFORE_DEPARTURE_MS = 30_000;
  /**
   * On pedestrian leg, start vehicle/boarding API checks when this close to the next transit boarding stop.
   * Kept at 100 m — stop proximity was already reliable; wider radius is for boarding/stop closeness above.
   */
  private readonly NEAR_TRANSIT_BOARDING_PRECHECK_METERS = 100;
  private readonly PEDESTRIAN_SECTION_COMPLETION_DISTANCE_METERS = 50; // 50 meters to consider pedestrian section completed
  /**
   * Auto “alighted” from the current transit leg when user GPS stays this close to the
   * scheduled arrival stop (no explicit “I got off” UI). Consecutive ticks reduce GPS spikes.
   */
  private readonly AUTO_ALIGHT_ARRIVAL_METERS = 75;
  private readonly AUTO_ALIGHT_CONSECUTIVE_TICKS = 2;
  /** If horizontal accuracy is worse than this (m), do not advance auto-alight streak. */
  private readonly AUTO_ALIGHT_MAX_ACCURACY_METERS = 120;
  /**
   * Still flagged on-board at this leg's arrival hub, but schedule API vehicle is far from the user:
   * likely stale vehicle from the previous leg — clear so transfer gets approaching + boarding flow.
   */
  private readonly STALE_ON_BUS_AT_ARRIVAL_HUB_METERS = 200;
  private readonly STALE_ON_BUS_VEHICLE_FAR_METERS = 600;

  shownNotifications: Set<string> = new Set();

  private debugProgressTick = 0;
  /** Monotonic streak while GPS stays within {@link AUTO_ALIGHT_ARRIVAL_METERS} of arrival stop. */
  private autoAlightProximityTicks = 0;
  /** Aborts the previous boarding vehicle request when switching to another stop/line. */
  private boardingNextLocationSub?: Subscription;
  /**
   * While GetNextRouteLocation for this `routeDesc::stopCode` is in flight, skip starting another —
   * avoids DevTools “(canceled)” spam when the ~3s tick fires before the server responds.
   */
  private boardingVehiclePollKeyInFlight: string | null = null;

  // State management
  private trackingState: TrackingState = {
    currentSectionIndex: 0,
    currentActionIndex: 0,
    isUserOnBus: false,
    boardedBusSectionIndex: null,
  };
  
  // Observable for UI synchronization
  private _trackingStateSubject = new BehaviorSubject<TrackingState>(this.trackingState);
  public trackingState$ = this._trackingStateSubject.asObservable();
  
  // Subject for automatic section switching in UI
  private _sectionChangeSubject = new Subject<{ sectionIndex: number; actionIndex?: number }>();
  public sectionChange$ = this._sectionChangeSubject.asObservable();
  
  // Subject for showing bus boarding popup
  private _showBoardBusPopupSubject = new Subject<{ section: Section; busLocation: Coordinate }>();
  public showBoardBusPopup$ = this._showBoardBusPopupSubject.asObservable();

  // Runtime diagnostics for mobile (debug overlay)
  private _boardingDebugStateSubject = new BehaviorSubject<BoardingDebugState>({
    updatedAt: new Date().toISOString(),
    debugTick: 0,
    trackingSectionIndex: 0,
    detectedSectionIndex: null,
    boardingCheckSectionIndex: null,
    currentSectionType: null,
    isUserOnBus: false,
    boardedBusSectionIndex: null,
    userLocation: null,
    gpsAccuracyMeters: null,
    stopLocation: null,
    busLocation: null,
    distanceToBusMeters: null,
    distanceToStopMeters: null,
    closeToBus: false,
    closeToStop: false,
    busDeparted: null,
    minutesSinceDeparture: null,
    hasShownPopup: false,
    popupEmitted: false,
    routeDesc: null,
    stopCode: null,
    transportLineLabel: null,
    triggerSource: 'unknown',
    skipReason: null,
    routeId: null,
    routeSectionsCount: null,
    pedestrianToArrivalMeters: null,
    distanceToNextTransitBoardingStopMeters: null,
    withinNextBoardingStopPrecheck: false,
    scheduledDepartureIso: null,
    scheduledDepartureLocal: null,
    deviceNowLocal: '',
    deviceTimeZone: '',
    lastScheduleApiMessage: null,
    shownNotificationsCount: 0,
    checkIntervalMs: 3000,
    thrBusBoardingM: 50,
    thrPedCompleteM: 50,
    thrNearStopPrecheckM: 100,
  });
  public boardingDebugState$ = this._boardingDebugStateSubject.asObservable();

  /** Latest vehicle coordinates from schedule API (boarding checks). Map UI can draw without re-deriving from sections. */
  private readonly _latestBusLocationSubject = new BehaviorSubject<Coordinate | null>(
    null
  );
  public readonly latestBusLocation$ =
    this._latestBusLocationSubject.asObservable();

  constructor(
    private readonly routeTrackingBannerService: RouteTrackingBannerService,
    private readonly userLocationService: UserLocationService,
    private readonly mapService: MapService,
    private readonly languageService: LanguageService,
    private readonly audioService: AudioService,
    private readonly modalService: ModalService,
    private readonly scheduleService: ScheduleService
  ) {}

  startTracking(route: RoutePlanning): void {
    LogService.logInfo('Starting route tracking');
    this.stopTracking();
    // Set up new tracking
    this.currentRoute = route;
    
    // Reset state
    this.trackingState = {
      currentSectionIndex: 0,
      currentActionIndex: 0,
      isUserOnBus: false,
      boardedBusSectionIndex: null,
    };
    this._trackingStateSubject.next(this.trackingState);
    
    // Reset shown notifications when starting a new route
    this.shownNotifications.clear();
    this.debugProgressTick = 0;
    this.autoAlightProximityTicks = 0;

    // Start tracking with interval checks - track ALL sections
    // Use switchMap to ensure only one check runs at a time
    // IMPORTANT: use checkUserLocationAndProgress() — it also runs boarding checks while still
    // on a pedestrian leg but within range of the next transit boarding stop (dead copy was removed).
    this.trackingSubscription = interval(this.CHECK_INTERVAL_MS)
      .pipe(
        takeWhile(() => !!this.currentRoute),
        switchMap(() => this.checkUserLocationAndProgress())
      )
      .subscribe();
  }
  
  // Method to confirm bus boarding
  confirmBoardingBus(sectionIndex: number): void {
    LogService.logInfo('User confirmed boarding bus', { sectionIndex });
    this.trackingState.isUserOnBus = true;
    this.trackingState.boardedBusSectionIndex = sectionIndex;
    this.trackingState.currentSectionIndex = sectionIndex;
    this.autoAlightProximityTicks = 0;
    // Drop stale GPS from pre-boarding checks — map poll will refresh for this transit leg.
    this._latestBusLocationSubject.next(null);
    this._trackingStateSubject.next(this.trackingState);
  }
  
  // Method to cancel bus boarding
  cancelBoardingBus(): void {
    LogService.logInfo('User cancelled boarding bus');
    // Stay on current section, but don't mark as boarded bus
  }
  
  // Get current tracking state
  getTrackingState(): TrackingState {
    return { ...this.trackingState };
  }

  ngOnDestroy(): void {
    this.stopTracking();
    this.shownNotifications.clear();
  }

  stopTracking(): void {
    if (this.trackingSubscription) {
      this.trackingSubscription.unsubscribe();
      this.trackingSubscription = null;
    }

    this.currentRoute = null;
    this.shownNotifications.clear();
    this._latestBusLocationSubject.next(null);
    this.routeTrackingBannerService.dismiss();
    this.autoAlightProximityTicks = 0;
    this.boardingNextLocationSub?.unsubscribe();
    this.boardingNextLocationSub = undefined;
    this.boardingVehiclePollKeyInFlight = null;
  }

  // Determines current section based on user location
  private getCurrentSectionBasedOnLocation(userLocation: Location | null): Section | null {
    if (!this.currentRoute?.sections?.length || !userLocation) {
      return this.currentRoute?.sections?.[this.trackingState.currentSectionIndex] || null;
    }

    // Check sections starting from current
    for (let i = this.trackingState.currentSectionIndex; i < this.currentRoute.sections.length; i++) {
      const section = this.currentRoute.sections[i];
      
      // For pedestrian sections - check if we've reached arrival point
      if (section.type === eSectionType.PEDESTRIAN) {
        if (section.arrival?.place?.location) {
          const distance = this.calculateDistance(
            userLocation.lat,
            userLocation.lng,
            section.arrival.place.location.lat,
            section.arrival.place.location.lng
          );
          
          // If haven't reached arrival point yet - this is current section
          if (distance > this.PEDESTRIAN_SECTION_COMPLETION_DISTANCE_METERS) {
            return section;
          }
        } else {
          // If no arrival point, check by time
          if (section.arrival?.time) {
            const arrivalTime = section.arrival.time instanceof Date 
              ? section.arrival.time 
              : new Date(section.arrival.time);
            const now = new Date();
            
            // If arrival time hasn't come yet - this is current section
            if (arrivalTime.getTime() > now.getTime()) {
              return section;
            }
          } else {
            return section;
          }
        }
      }
      
      // For transit sections - check if we're on the route
      if (section.type === eSectionType.TRANSIT) {
        // If user already boarded this bus - this is current section
        if (this.trackingState.isUserOnBus && 
            this.trackingState.boardedBusSectionIndex === i) {
          return section;
        }
        
        // Waiting for this leg or on board without stale flags: stay on this TRANSIT until the
        // scheduled *arrival* instant has passed. Using only `departure > now` skips the whole leg
        // after a slightly late departure — wrong at transfer stops (looks like "already on next bus").
        if (section.departure?.time) {
          const arrivalTime = section.arrival?.time
            ? section.arrival.time instanceof Date
              ? section.arrival.time
              : new Date(section.arrival.time)
            : null;
          const now = new Date();

          if (!arrivalTime || now.getTime() <= arrivalTime.getTime()) {
            return section;
          }
        } else {
          return section;
        }
      }
    }
    
    // If passed all sections, return the last one
    return this.currentRoute.sections[this.currentRoute.sections.length - 1] || null;
  }

  /**
   * If the plan / GPS has moved to a **later** section than the transit leg we marked as boarded,
   * drop the on-bus flags. Otherwise we stay "on bus" for leg A while the UI and map are already on
   * leg B — no second boarding popup, no "bus approaching", and the vehicle marker follows the wrong leg.
   */
  private clearStaleOnBusStateIfAdvancedPastBoardedTransit(): void {
    if (!this.currentRoute?.sections?.length) {
      return;
    }
    const boarded = this.trackingState.boardedBusSectionIndex;
    if (!this.trackingState.isUserOnBus || boarded == null) {
      return;
    }
    const cur = this.trackingState.currentSectionIndex;
    if (cur > boarded) {
      LogService.logInfo(
        'Clearing stale on-bus state — current section index passed boarded transit leg',
        { currentSectionIndex: cur, boardedBusSectionIndex: boarded }
      );
      this.trackingState.isUserOnBus = false;
      this.trackingState.boardedBusSectionIndex = null;
      this._latestBusLocationSubject.next(null);
      this._trackingStateSubject.next(this.trackingState);
    }
  }

  /**
   * Same section index as {@link TrackingState.boardedBusSectionIndex} but user is at the arrival
   * hub while the polled vehicle is far — typical after transfer when the API still returns the
   * previous bus or we never auto-alighted due to GPS accuracy.
   */
  private clearStaleOnBusStateIfAtArrivalHubButTrackedVehicleFar(
    userLocation: Location,
    section: Section
  ): void {
    if (
      section.type !== eSectionType.TRANSIT ||
      !section.arrival?.place?.location ||
      !this.trackingState.isUserOnBus ||
      this.trackingState.boardedBusSectionIndex !== this.trackingState.currentSectionIndex
    ) {
      return;
    }
    const bus = this._latestBusLocationSubject.getValue();
    if (bus == null || bus.lat == null || bus.lng == null) {
      return;
    }
    const distToArrival = this.calculateDistance(
      userLocation.lat,
      userLocation.lng,
      section.arrival.place.location.lat,
      section.arrival.place.location.lng
    );
    const distUserBus = this.calculateDistance(
      userLocation.lat,
      userLocation.lng,
      bus.lat,
      bus.lng
    );
    if (
      distToArrival <= this.STALE_ON_BUS_AT_ARRIVAL_HUB_METERS &&
      distUserBus >= this.STALE_ON_BUS_VEHICLE_FAR_METERS
    ) {
      LogService.logInfo('Clearing on-bus — at arrival hub but reported vehicle far from user', {
        distToArrival,
        distUserBus,
        sectionIndex: this.trackingState.currentSectionIndex,
      });
      this.trackingState.isUserOnBus = false;
      this.trackingState.boardedBusSectionIndex = null;
      this._latestBusLocationSubject.next(null);
      this._trackingStateSubject.next(this.trackingState);
    }
  }

  // Get current active section
  private getCurrentSection(): Section | null {
    if (!this.currentRoute?.sections?.length) return null;
    
    const sectionIndex = this.trackingState.currentSectionIndex;
    if (sectionIndex >= 0 && sectionIndex < this.currentRoute.sections.length) {
      return this.currentRoute.sections[sectionIndex];
    }
    
    return null;
  }

  // Main method for checking location and progress
  private checkUserLocationAndProgress(): Observable<void> {
    return this.userLocationService.$userLocationChanged.pipe(
      take(1), // Take only first emission to avoid multiple simultaneous checks
      map(({ location }) => {
        if (!location || !this.currentRoute) return;
        
        // Determine current section based on location
        const detectedSection = this.getCurrentSectionBasedOnLocation(location);
        if (!detectedSection) return;
        
        const detectedSectionIndex = this.currentRoute.sections.findIndex(s => s === detectedSection);
        
        // Update current section if it changed
        if (detectedSectionIndex !== this.trackingState.currentSectionIndex && detectedSectionIndex >= 0) {
          this.updateCurrentSection(detectedSectionIndex);
        }

        this.clearStaleOnBusStateIfAdvancedPastBoardedTransit();

        let currentSection = this.getCurrentSection();
        if (!currentSection) return;

        if (
          currentSection.type === eSectionType.TRANSIT &&
          currentSection.arrival?.place?.location
        ) {
          this.clearStaleOnBusStateIfAtArrivalHubButTrackedVehicleFar(location, currentSection);
          currentSection = this.getCurrentSection() ?? currentSection;
        }

        this.debugProgressTick++;

        let pedestrianToArrivalMeters: number | null = null;
        if (
          currentSection.type === eSectionType.PEDESTRIAN &&
          currentSection.arrival?.place?.location
        ) {
          pedestrianToArrivalMeters = this.calculateDistance(
            location.lat,
            location.lng,
            currentSection.arrival.place.location.lat,
            currentSection.arrival.place.location.lng
          );
        }

        let distanceToNextTransitBoardingStopMeters: number | null = null;
        let withinNextBoardingStopPrecheck = false;
        if (
          currentSection.type === eSectionType.PEDESTRIAN &&
          detectedSectionIndex < this.currentRoute.sections.length - 1
        ) {
          const nextS = this.currentRoute.sections[detectedSectionIndex + 1];
          if (
            nextS?.type === eSectionType.TRANSIT &&
            nextS.departure?.place?.location
          ) {
            distanceToNextTransitBoardingStopMeters = this.calculateDistance(
              location.lat,
              location.lng,
              nextS.departure.place.location.lat,
              nextS.departure.place.location.lng
            );
            withinNextBoardingStopPrecheck =
              distanceToNextTransitBoardingStopMeters <=
              this.NEAR_TRANSIT_BOARDING_PRECHECK_METERS;
          }
        }

        this.pushBoardingDebug({
          debugTick: this.debugProgressTick,
          detectedSectionIndex,
          boardingCheckSectionIndex: null,
          currentSectionType: currentSection.type,
          userLocation: { lat: location.lat, lng: location.lng },
          gpsAccuracyMeters:
            location.accuracyMeters != null &&
            !Number.isNaN(location.accuracyMeters)
              ? location.accuracyMeters
              : null,
          popupEmitted: false,
          skipReason: null,
          routeId: this.currentRoute.routeId,
          routeSectionsCount: this.currentRoute.sections.length,
          pedestrianToArrivalMeters,
          distanceToNextTransitBoardingStopMeters,
          withinNextBoardingStopPrecheck,
        });

        // Handle different section types
        if (currentSection.type === eSectionType.PEDESTRIAN) {
          this.handlePedestrianSection(location, currentSection, detectedSectionIndex);
        } else if (currentSection.type === eSectionType.TRANSIT) {
          this.handleTransitSection(
            location,
            currentSection,
            this.trackingState.currentSectionIndex
          );
        }
        
        // Also check next TRANSIT section if user is approaching it (for bus boarding popup)
        // This ensures popup shows even if user is still on pedestrian section but close to bus stop
        if (currentSection.type === eSectionType.PEDESTRIAN && detectedSectionIndex < this.currentRoute.sections.length - 1) {
          const nextSection = this.currentRoute.sections[detectedSectionIndex + 1];
          if (nextSection.type === eSectionType.TRANSIT) {
            if (nextSection.departure?.place?.location) {
              const distanceToBoardingStop = this.calculateDistance(
                location.lat,
                location.lng,
                nextSection.departure.place.location.lat,
                nextSection.departure.place.location.lng
              );

              if (
                distanceToBoardingStop <= this.NEAR_TRANSIT_BOARDING_PRECHECK_METERS
              ) {
                this.checkBusBoardingConditions(
                  location,
                  nextSection,
                  detectedSectionIndex + 1,
                  'pedestrian-near-stop'
                );
              }
            }
          }
        }
      })
    );
  }
  
  // Update current section and notify UI
  private updateCurrentSection(sectionIndex: number, actionIndex: number = 0): void {
    if (sectionIndex !== this.trackingState.currentSectionIndex) {
      LogService.logInfo('Updating current section', { 
        from: this.trackingState.currentSectionIndex, 
        to: sectionIndex 
      });
      
      this.trackingState.currentSectionIndex = sectionIndex;
      this.trackingState.currentActionIndex = actionIndex;
      this._trackingStateSubject.next(this.trackingState);
      
      // Notify UI about section switch requirement
      this._sectionChangeSubject.next({ sectionIndex, actionIndex });
    }
  }
  
  // Handle pedestrian section
  private handlePedestrianSection(
    userLocation: Location,
    section: Section,
    sectionIndex: number
  ): void {
    // Check if we've reached arrival point
    if (section.arrival?.place?.location) {
      const distance = this.calculateDistance(
        userLocation.lat,
        userLocation.lng,
        section.arrival.place.location.lat,
        section.arrival.place.location.lng
      );
      
      // If reached arrival point - switch to next section
      if (distance <= this.PEDESTRIAN_SECTION_COMPLETION_DISTANCE_METERS) {
        const nextSectionIndex = sectionIndex + 1;
        if (nextSectionIndex < this.currentRoute.sections.length) {
          this.updateCurrentSection(nextSectionIndex);
        }
      }
    }
  }

  /**
   * When the user had confirmed boarding this transit leg, infer alighting from GPS near the
   * scheduled arrival stop (no “did you get off?” prompt). Clears on-bus state and advances to
   * the next section so transfers and the next boarding flow work.
   *
   * @returns true if alighting was applied this tick (caller should skip same-tick follow-ups).
   */
  private tryAutoAlightFromTransit(
    section: Section,
    userLocation: Location,
    sectionIndex: number
  ): boolean {
    if (
      section.type !== eSectionType.TRANSIT ||
      !section.arrival?.place?.location
    ) {
      this.autoAlightProximityTicks = 0;
      return false;
    }

    const distToArrival = this.calculateDistance(
      userLocation.lat,
      userLocation.lng,
      section.arrival.place.location.lat,
      section.arrival.place.location.lng
    );

    const acc = userLocation.accuracyMeters;
    if (
      distToArrival > this.AUTO_ALIGHT_ARRIVAL_METERS &&
      acc != null &&
      !Number.isNaN(acc) &&
      acc > this.AUTO_ALIGHT_MAX_ACCURACY_METERS
    ) {
      this.autoAlightProximityTicks = 0;
      LogService.logDebug('Auto-alight skipped — GPS accuracy too poor', {
        accuracyMeters: acc,
        max: this.AUTO_ALIGHT_MAX_ACCURACY_METERS,
      });
      return false;
    }

    if (distToArrival <= this.AUTO_ALIGHT_ARRIVAL_METERS) {
      this.autoAlightProximityTicks++;
    } else {
      this.autoAlightProximityTicks = 0;
    }

    LogService.logDebug('Auto-alight proximity streak', {
      sectionIndex,
      distToArrival,
      ticks: this.autoAlightProximityTicks,
      needTicks: this.AUTO_ALIGHT_CONSECUTIVE_TICKS,
      thresholdM: this.AUTO_ALIGHT_ARRIVAL_METERS,
    });

    if (this.autoAlightProximityTicks < this.AUTO_ALIGHT_CONSECUTIVE_TICKS) {
      return false;
    }

    this.autoAlightProximityTicks = 0;
    LogService.logInfo('Auto-alight: GPS near arrival stop for boarded transit leg', {
      sectionIndex,
      distToArrival,
    });

    this.trackingState.isUserOnBus = false;
    this.trackingState.boardedBusSectionIndex = null;
    this._latestBusLocationSubject.next(null);

    const nextIdx = sectionIndex + 1;
    if (this.currentRoute && nextIdx < this.currentRoute.sections.length) {
      this.updateCurrentSection(nextIdx, 0);
    } else {
      this._trackingStateSubject.next(this.trackingState);
    }
    return true;
  }

  // Handle transit section
  private handleTransitSection(
    userLocation: Location,
    section: Section,
    sectionIndex: number
  ): void {
    // If user already boarded bus - check notifications
    if (this.trackingState.isUserOnBus && 
        this.trackingState.boardedBusSectionIndex === sectionIndex) {
      if (this.tryAutoAlightFromTransit(section, userLocation, sectionIndex)) {
        return;
      }
      // Check drop-off notification (use current fix — no extra $userLocationChanged sub per tick)
      if (section.arrival?.time) {
        this.checkDropOffNotification(section.arrival.time, section, userLocation);
      }
      return;
    }
    
    // If haven't boarded yet - check conditions for showing boarding popup
    this.checkBusBoardingConditions(userLocation, section, sectionIndex, 'transit');
    
    // “Bus approaching”: scheduled window and/or user ↔ vehicle from schedule API
    if (section.departure?.time) {
      this.tryEmitBusApproachingNotification(
        section.departure.time,
        userLocation,
        null
      );
    }
  }
  
  // Check conditions for showing bus boarding popup (SR-101.3.1)
  private checkBusBoardingConditions(
    userLocation: Location,
    section: Section,
    sectionIndex: number,
    triggerSource: 'transit' | 'pedestrian-near-stop' = 'transit'
  ): void {
    const transportLineLabel =
      (
        section.transport?.shortName ||
        section.transport?.name ||
        section.transport?.longName ||
        section.transport?.description ||
        ''
      ).trim() || null;
    const scheduledDepartureIso =
      section.departure?.time != null
        ? new Date(section.departure.time as Date | string | number).toISOString()
        : null;

    this.pushBoardingDebug({
      triggerSource,
      boardingCheckSectionIndex: sectionIndex,
      currentSectionType: section.type,
      userLocation: { lat: userLocation.lat, lng: userLocation.lng },
      gpsAccuracyMeters:
        userLocation.accuracyMeters != null &&
        !Number.isNaN(userLocation.accuracyMeters)
          ? userLocation.accuracyMeters
          : null,
      stopLocation: section.departure?.place?.location
        ? {
            lat: section.departure.place.location.lat,
            lng: section.departure.place.location.lng,
          }
        : null,
      routeDesc: section.transport?.description || null,
      stopCode: section.departure?.place?.code || null,
      transportLineLabel,
      scheduledDepartureIso,
      lastScheduleApiMessage: null,
      popupEmitted: false,
      skipReason: null,
    });
    // Only skip boarding UI/API for the **same** transit leg we already confirmed — not for the next bus after transfer.
    if (
      this.trackingState.isUserOnBus &&
      this.trackingState.boardedBusSectionIndex === sectionIndex
    ) {
      LogService.logDebug(
        'Bus boarding check skipped - already on board for this transit leg',
        { sectionIndex }
      );
      this.pushBoardingDebug({ skipReason: 'user_already_on_bus_this_leg' });
      return;
    }

    // Check if bus has passed boarding stop
    if (!section.departure?.time || !section.departure?.place?.location) {
      LogService.logDebug('Bus boarding check skipped - missing departure time or location', {
        hasTime: !!section.departure?.time,
        hasLocation: !!section.departure?.place?.location
      });
      this.pushBoardingDebug({ skipReason: 'missing_departure_time_or_location' });
      return;
    }

    // Still marked on an earlier leg (e.g. before auto-alight catches up): only evaluate the next
    // bus if GPS is already at **this** leg's boarding stop — avoids prompting for bus 2 while on bus 1.
    if (this.trackingState.isUserOnBus) {
      const dep = section.departure.place.location;
      const distToBoarding = this.calculateDistance(
        userLocation.lat,
        userLocation.lng,
        dep.lat,
        dep.lng
      );
      if (distToBoarding > this.BUS_BOARDING_DISTANCE_METERS) {
        LogService.logDebug(
          'Bus boarding check skipped — on earlier leg in state; not at this boarding stop',
          {
            sectionIndex,
            boardedSectionIndex: this.trackingState.boardedBusSectionIndex,
            distToBoardingMeters: distToBoarding,
            thresholdM: this.BUS_BOARDING_DISTANCE_METERS,
          }
        );
        this.pushBoardingDebug({
          skipReason: 'on_bus_prev_leg_far_from_this_boarding_stop',
        });
        return;
      }
    }

    const departureTime = section.departure.time instanceof Date
      ? section.departure.time
      : new Date(section.departure.time);
    const routeDesc = section.transport.description;
    const stopCode = section.departure.place.code;

    if (!routeDesc || !stopCode) {
      LogService.logDebug('Bus boarding check skipped - missing routeDesc or stopCode', {
        routeDesc,
        stopCode,
      });
      const tMissing = new Date();
      const minutesSinceDepMissing =
        (tMissing.getTime() - departureTime.getTime()) / (1000 * 60);
      this.pushBoardingDebug({
        skipReason: 'missing_routeDesc_or_stopCode',
        busDeparted: departureTime.getTime() <= tMissing.getTime(),
        minutesSinceDeparture: minutesSinceDepMissing,
      });
      return;
    }

    // Always request vehicle position while waiting at the stop — do NOT gate the API on
    // scheduled departure time. Otherwise debug shows no bus coords and it looks like the API
    // "never runs". Popup visibility still uses departure time below.
    const vehiclePollKey = `${routeDesc}::${stopCode}`;
    if (this.boardingVehiclePollKeyInFlight === vehiclePollKey) {
      this.pushBoardingDebug({
        skipReason: 'next_route_location_same_leg_in_flight',
      });
      return;
    }
    this.boardingNextLocationSub?.unsubscribe();
    this.boardingVehiclePollKeyInFlight = vehiclePollKey;
    this.boardingNextLocationSub = this.scheduleService
      .getNextRouteLocation(routeDesc, stopCode)
      .pipe(
        take(1),
        finalize(() => {
          if (this.boardingVehiclePollKeyInFlight === vehiclePollKey) {
            this.boardingVehiclePollKeyInFlight = null;
          }
        })
      )
      .subscribe({
      next: (response) => {
        const now = new Date();
        const minutesSinceDeparture =
          (now.getTime() - departureTime.getTime()) / (1000 * 60);
        const busDeparted = departureTime.getTime() <= now.getTime();

        const distanceToStopForDebug = this.calculateDistance(
          userLocation.lat,
          userLocation.lng,
          section.departure.place.location.lat,
          section.departure.place.location.lng
        );
        const closeToStopForDebug =
          distanceToStopForDebug <= this.BUS_BOARDING_DISTANCE_METERS;

        if (!response.success) {
          LogService.logDebug('Bus boarding check - API call failed', {
            success: response.success,
            message: response.message,
          });
          this.pushBoardingDebug({
            busDeparted,
            minutesSinceDeparture,
            busLocation: null,
            distanceToBusMeters: null,
            closeToBus: false,
            distanceToStopMeters: distanceToStopForDebug,
            closeToStop: closeToStopForDebug,
            lastScheduleApiMessage: response.message ?? '(no message)',
            skipReason: 'next_route_location_api_failed',
          });
          return;
        }

        if (!response.data) {
          LogService.logDebug('Bus boarding check - no bus location data returned');
          this.pushBoardingDebug({
            busDeparted,
            minutesSinceDeparture,
            busLocation: null,
            distanceToBusMeters: null,
            closeToBus: false,
            distanceToStopMeters: distanceToStopForDebug,
            closeToStop: closeToStopForDebug,
            lastScheduleApiMessage: response.message ?? 'success:no_data',
            skipReason: 'no_bus_location_data',
          });
          return;
        }

        const busLocation = response.data;
        this._latestBusLocationSubject.next({
          lat: busLocation.lat,
          lng: busLocation.lng,
        });

        if (section.departure?.time) {
          this.tryEmitBusApproachingNotification(
            section.departure.time,
            userLocation,
            busLocation
          );
        }

        const distance = this.calculateDistance(
          userLocation.lat,
          userLocation.lng,
          busLocation.lat,
          busLocation.lng
        );

        const distanceToStop = this.calculateDistance(
          userLocation.lat,
          userLocation.lng,
          section.departure.place.location.lat,
          section.departure.place.location.lng
        );

        const popupKey = `board_bus_${sectionIndex}_${section.departure.time}`;
        const hasShownPopup = this.shownNotifications.has(popupKey);

        const isCloseToBus = distance <= this.BUS_BOARDING_DISTANCE_METERS;
        const isCloseToStop = distanceToStop <= this.BUS_BOARDING_DISTANCE_METERS;

        LogService.logInfo('Bus boarding check:', {
          distance,
          distanceToStop,
          threshold: this.BUS_BOARDING_DISTANCE_METERS,
          sectionIndex,
          routeDesc,
          stopCode,
          minutesSinceDeparture: minutesSinceDeparture.toFixed(2),
          busDeparted,
          hasShownPopup,
          userLocation: { lat: userLocation.lat, lng: userLocation.lng },
          busLocation: { lat: busLocation.lat, lng: busLocation.lng },
          stopLocation: {
            lat: section.departure.place.location.lat,
            lng: section.departure.place.location.lng,
          },
        });

        // Live debug: always show distances and bus position when API returned data
        this.pushBoardingDebug({
          busDeparted,
          minutesSinceDeparture,
          busLocation: { lat: busLocation.lat, lng: busLocation.lng },
          distanceToBusMeters: distance,
          distanceToStopMeters: distanceToStop,
          closeToBus: isCloseToBus,
          closeToStop: isCloseToStop,
          hasShownPopup,
          lastScheduleApiMessage: response.message ?? 'ok',
          skipReason: null,
        });

        const msUntilDeparture = departureTime.getTime() - now.getTime();
        const stopTimeAllowsBoardingPrompt =
          busDeparted ||
          msUntilDeparture <= this.BOARDING_STOP_TIME_WINDOW_BEFORE_DEPARTURE_MS;
        // Near vehicle OR at stop when schedule says boarding is plausible (not e.g. 12 min early at stop only).
        const closeEnough =
          isCloseToBus || (isCloseToStop && stopTimeAllowsBoardingPrompt);

        if (!busDeparted && !closeEnough) {
          LogService.logDebug('Bus boarding - waiting for scheduled departure (no popup yet)', {
            departureTime: departureTime.toISOString(),
            now: now.toISOString(),
            secondsUntilDeparture: msUntilDeparture / 1000,
          });
          this.pushBoardingDebug({
            skipReason: 'bus_not_departed_yet',
            popupEmitted: false,
          });
          return;
        }

        // Stale window vs *scheduled* departure in route data (not “user was late”): if that instant is
        // already >10 min in the past and the user is not near bus/stop, do not prompt.
        if (minutesSinceDeparture > 10 && !closeEnough) {
          LogService.logDebug(
            'Bus boarding skipped — >10 min after scheduled departure time in route data, user not near',
            { minutesSinceDeparture }
          );
          this.pushBoardingDebug({
            skipReason: 'past_scheduled_departure_10m_not_near',
            popupEmitted: false,
          });
          return;
        }

        if (closeEnough && !hasShownPopup) {
          LogService.logInfo('Showing bus boarding popup', {
            isCloseToBus,
            isCloseToStop,
            distance,
            distanceToStop,
          });
          this.shownNotifications.add(popupKey);
          this.pushBoardingDebug({
            popupEmitted: true,
            hasShownPopup: true,
            skipReason: null,
          });
          this._showBoardBusPopupSubject.next({ section, busLocation });
        } else {
          LogService.logDebug('Bus boarding popup not shown', {
            isCloseToBus,
            isCloseToStop,
            hasShownPopup,
            distance,
            distanceToStop,
          });
          this.pushBoardingDebug({
            popupEmitted: false,
            skipReason: hasShownPopup
              ? 'popup_already_shown'
              : 'not_within_distance_threshold',
          });
        }
      },
      error: (error) => {
        LogService.logError('Error getting bus location:', error);
        const nowErr = new Date();
        const minutesSinceDepErr =
          (nowErr.getTime() - departureTime.getTime()) / (1000 * 60);
        const distStopErr = this.calculateDistance(
          userLocation.lat,
          userLocation.lng,
          section.departure.place.location.lat,
          section.departure.place.location.lng
        );
        this.pushBoardingDebug({
          busDeparted: departureTime.getTime() <= nowErr.getTime(),
          minutesSinceDeparture: minutesSinceDepErr,
          busLocation: null,
          distanceToBusMeters: null,
          closeToBus: false,
          distanceToStopMeters: distStopErr,
          closeToStop: distStopErr <= this.BUS_BOARDING_DISTANCE_METERS,
          skipReason: 'next_route_location_network_error',
          lastScheduleApiMessage:
            (error && (error as Error).message) || String(error),
        });
      }
    });
  }

  private pushBoardingDebug(partial: Partial<BoardingDebugState>): void {
    const current = this._boardingDebugStateSubject.getValue();
    const clock = new Date();
    const merged: BoardingDebugState = {
      ...current,
      ...partial,
      updatedAt: clock.toISOString(),
      deviceNowLocal: clock.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'medium',
      }),
      deviceTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      trackingSectionIndex: this.trackingState.currentSectionIndex,
      isUserOnBus: this.trackingState.isUserOnBus,
      boardedBusSectionIndex: this.trackingState.boardedBusSectionIndex,
      shownNotificationsCount: this.shownNotifications.size,
      checkIntervalMs: this.CHECK_INTERVAL_MS,
      thrBusBoardingM: this.BUS_BOARDING_DISTANCE_METERS,
      thrPedCompleteM: this.PEDESTRIAN_SECTION_COMPLETION_DISTANCE_METERS,
      thrNearStopPrecheckM: this.NEAR_TRANSIT_BOARDING_PRECHECK_METERS,
    };
    const schedIso = merged.scheduledDepartureIso;
    merged.scheduledDepartureLocal = schedIso
      ? new Date(schedIso).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'medium',
        })
      : null;
    this._boardingDebugStateSubject.next(merged);
  }

  private isUserOnTransitRoute(
    userLocation: Location,
    section: Section
  ): boolean {
    if (!section.polyline) {
      LogService.logDebug('No polyline data available for section');
      return false;
    }

    try {
      // Convert the polyline string to a LineString using HERE Maps API
      const lineString = H.geo.LineString.fromFlexiblePolyline(
        section.polyline
      );

      // Create a point from user location
      const userPoint = new H.geo.Point(userLocation.lat, userLocation.lng);

      // Calculate the distance from the user to the polyline
      const distance = lineString
        .getLatLngAltArray()
        .reduce((minDistance, lat, index) => {
          if (index % 3 !== 0) return minDistance; // Skip altitude values

          const lng = lineString.getLatLngAltArray()[index + 1];
          const point = new H.geo.Point(lat, lng);
          const currentDistance = userPoint.distance(point);
          return Math.min(minDistance, currentDistance);
        }, Infinity);

      LogService.logDebug('Distance from route:', {
        distance,
        threshold: this.MAX_DISTANCE_FROM_ROUTE_METERS,
        isOnRoute: distance <= this.MAX_DISTANCE_FROM_ROUTE_METERS,
      });

      return distance <= this.MAX_DISTANCE_FROM_ROUTE_METERS;
    } catch (error) {
      LogService.logError('Error checking user location against route:', error);
      return false;
    }
  }

  /**
   * One-shot “bus approaching your stop” when either:
   * - scheduled departure is within {@link NOTIFICATION_THRESHOLD_MINUTES}, or
   * - schedule API vehicle is within {@link BUS_APPROACHING_PROXIMITY_METERS} of the user (early bus).
   */
  private tryEmitBusApproachingNotification(
    scheduledDepartureTime: Date | string | number,
    userLocation: Location,
    busLocation: Coordinate | null
  ): void {
    const departureTime =
      scheduledDepartureTime instanceof Date
        ? scheduledDepartureTime
        : new Date(scheduledDepartureTime);

    const now = new Date();
    const minutesUntilDeparture =
      (departureTime.getTime() - now.getTime()) / (1000 * 60);

    let closeByLivePosition = false;
    if (
      busLocation != null &&
      busLocation.lat != null &&
      busLocation.lng != null
    ) {
      const m = this.calculateDistance(
        userLocation.lat,
        userLocation.lng,
        busLocation.lat,
        busLocation.lng
      );
      closeByLivePosition = m <= this.BUS_APPROACHING_PROXIMITY_METERS;
    }

    LogService.logDebug('Bus approaching check:', {
      scheduledDepartureTime,
      departureTime: departureTime.toISOString(),
      now: now.toISOString(),
      minutesUntilDeparture,
      thresholdMinutes: this.NOTIFICATION_THRESHOLD_MINUTES,
      proximityMetersThreshold: this.BUS_APPROACHING_PROXIMITY_METERS,
      closeByLivePosition,
      hasBusCoords: busLocation != null,
    });

    const notificationKey = `arrival_${departureTime.getTime()}`;
    if (this.shownNotifications.has(notificationKey)) {
      return;
    }

    const withinScheduleWindow =
      minutesUntilDeparture <= this.NOTIFICATION_THRESHOLD_MINUTES &&
      minutesUntilDeparture > 0;

    if (!withinScheduleWindow && !closeByLivePosition) {
      return;
    }

    LogService.logInfo('Showing arrival notification', {
      withinScheduleWindow,
      closeByLivePosition,
    });
    const language = this.languageService.getSelectedLanguage();
    this.routeTrackingBannerService.show(BUS_APPROACHING[language.language], {
      ariaLive: 'assertive',
    });
    this.audioService.playNotification();
    this.shownNotifications.add(notificationKey);
  }

  /**
   * Reminder to alight: scheduled arrival window and/or near drop-off stop.
   * Does not change tracking section or clear isUserOnBus — user advances UI manually or via GPS on next leg.
   */
  private checkDropOffNotification(
    scheduledArrivalTime: Date | string | number,
    section: Section,
    userLocation: Location
  ): void {
    const arrivalTime =
      scheduledArrivalTime instanceof Date
        ? scheduledArrivalTime
        : new Date(scheduledArrivalTime);

    const now = new Date();
    const timeUntilDropOff = arrivalTime.getTime() - now.getTime();
    const minutesUntilDropOff = timeUntilDropOff / (1000 * 60);

    LogService.logDebug('Drop-off check:', {
      scheduledArrivalTime,
      arrivalTime,
      now,
      minutesUntilDropOff,
      threshold: this.NOTIFICATION_THRESHOLD_MINUTES,
    });

    const notificationKey = `dropoff_${arrivalTime.getTime()}`;

    if (!userLocation) {
      LogService.logDebug('No user location available');
      return;
    }

    const isOnRoute = this.isUserOnTransitRoute(userLocation, section);

    let isCloseToDropOff = false;
    if (section.arrival?.place?.location) {
      const dropOffPoint = section.arrival.place.location;
      const distanceToDropOff = this.calculateDistance(
        userLocation.lat,
        userLocation.lng,
        dropOffPoint.lat,
        dropOffPoint.lng
      );

      isCloseToDropOff = distanceToDropOff <= this.DROP_OFF_PROXIMITY_METERS;

      LogService.logDebug('Drop-off proximity check:', {
        distanceToDropOff,
        threshold: this.DROP_OFF_PROXIMITY_METERS,
        isCloseToDropOff,
      });
    }

    const shouldNotifyByTime =
      minutesUntilDropOff <= this.NOTIFICATION_THRESHOLD_MINUTES &&
      minutesUntilDropOff > 0;

    if (
      (shouldNotifyByTime || isCloseToDropOff) &&
      !this.shownNotifications.has(notificationKey) &&
      isOnRoute &&
      this.trackingState.isUserOnBus &&
      this.trackingState.boardedBusSectionIndex !== null &&
      this.currentRoute!.sections[this.trackingState.boardedBusSectionIndex] === section
    ) {
      LogService.logInfo('Showing drop-off notification');
      const language = this.languageService.getSelectedLanguage();
      this.routeTrackingBannerService.show(DROP_OFF_APPROACHING[language.language], {
        ariaLive: 'assertive',
      });
      this.audioService.playNotification();
      this.shownNotifications.add(notificationKey);
    }
  }

  // Calculate distance between two points using Haversine formula
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371e3; // Earth radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  }
}
